'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/app/providers';

const terminalLines = [
  { type: 'command', text: 'find /page --recursive' },
  { type: 'error', text: 'Error: ENOENT - page not found' },
  { type: 'command', text: 'git checkout page' },
  { type: 'error', text: "error: pathspec 'page' did not match" },
  { type: 'command', text: 'sudo make page-exist' },
  { type: 'output', text: "make: *** No rule to make target 'page-exist'" },
  { type: 'command', text: 'curl -s https://geekscreations.com/' },
  { type: 'success', text: '200 OK - Home is still there ✓' },
];

function TerminalTyping() {
  const [displayedLines, setDisplayedLines] = useState<{ type: string; text: string; }[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    if (currentLineIndex >= terminalLines.length) {
      setIsTyping(false);
      return;
    }

    const currentLine = terminalLines[currentLineIndex];
    
    if (currentCharIndex < currentLine.text.length) {
      const timeout = setTimeout(() => {
        setCurrentCharIndex(prev => prev + 1);
      }, 30 + Math.random() * 40); // Random typing speed for realism
      return () => clearTimeout(timeout);
    } else {
      // Line complete, move to next
      const timeout = setTimeout(() => {
        setDisplayedLines(prev => [...prev, currentLine]);
        setCurrentLineIndex(prev => prev + 1);
        setCurrentCharIndex(0);
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [currentLineIndex, currentCharIndex]);

  const currentLine = terminalLines[currentLineIndex];
  const typingText = currentLine?.text.slice(0, currentCharIndex) || '';

  return (
    <div className="font-mono text-left space-y-1 text-sm">
      {displayedLines.map((line, index) => (
        <p key={index} className={
          line.type === 'error' ? 'text-error-400' :
          line.type === 'success' ? 'text-success-400' :
          line.type === 'output' ? 'text-slate-400' :
          'text-brand-violet-300'
        }>
          <span className="text-slate-500">{line.type === 'command' ? '$ ' : '> '}</span>
          {line.text}
        </p>
      ))}
      {isTyping && currentLine && (
        <p className={
          currentLine.type === 'error' ? 'text-error-400' :
          currentLine.type === 'success' ? 'text-success-400' :
          currentLine.type === 'output' ? 'text-slate-400' :
          'text-brand-violet-300'
        }>
          <span className="text-slate-500">{currentLine.type === 'command' ? '$ ' : '> '}</span>
          {typingText}
          <span className="inline-block w-2 h-4 bg-brand-violet-300 ml-0.5 animate-[blink_1s_step-end_infinite]" />
        </p>
      )}
      {!isTyping && (
        <p className="text-brand-violet-300">
          <span className="text-slate-500">$ </span>
          <span className="inline-block w-2 h-4 bg-brand-violet-300 ml-0.5 animate-[blink_1s_step-end_infinite]" />
        </p>
      )}
    </div>
  );
}

export default function NotFound() {
  const t = useTranslations();

  return (
    <div className="min-h-screen hero-gradient flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <Link href="/" className="mb-12 relative z-10">
        <Image
          src="/GeeksCreations Logo.svg"
          alt="GeeksCreations"
          width={120}
          height={54}
          className="h-14 w-auto"
          priority
        />
      </Link>

      {/* Error content */}
      <div className="relative z-10 text-center max-w-2xl">
        {/* Glitch-style 404 */}
        <div className="relative mb-8">
          <h1 className="text-[8rem] md:text-[12rem] font-bold leading-none tracking-tighter text-white select-none">
            404
          </h1>
          <div className="absolute inset-0 flex items-center justify-center opacity-30 blur-md">
            <span className="text-[8rem] md:text-[12rem] font-bold leading-none tracking-tighter text-brand-blue">
              404
            </span>
          </div>
        </div>

        {/* Terminal-style message */}
        <div className="card-gradient-border rounded-2xl mb-10 mx-auto max-w-lg">
          <div className="p-6 rounded-2xl">
            <TerminalTyping />
          </div>
        </div>

        <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed">
          {t.errors?.notFound?.description || "The page you're looking for doesn't exist or has been moved."}
        </p>

        <Button asChild>
          <Link href="/">
            {t.errors?.notFound?.homeButton || 'Back to Home'}
          </Link>
        </Button>
      </div>

      {/* Decorative code lines */}
      <div className="absolute bottom-8 left-8 font-mono text-xs text-slate-600 hidden md:block">
        <p>{'// TODO: implement this route'}</p>
        <p>{'// console.log("Where did it go?")'}</p>
      </div>
    </div>
  );
}

